import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { Button, Card, Form } from 'react-bootstrap';
import { CreatePartnerIntegrationRequest } from './partnerIntegrationService';
import EditAuthCredentials from './EditAuthCredentials';
import ConfigurePartnerIntegrationModal from './ConfigurePartnerIntegrationModal';
import PartnerIntegration from 'domain/partnerIntegration';
import { getPartnerName, Partner } from 'features/settings/importMapping/partner';
import PartnerImportModal from './PartnerImportModal';
import { AgTraxConfigurationSettings } from './agTraxConfigurationSettings';
import { AgrisConfigurationSettings } from './agrisConfigurationSettings';
import { AgVantageConfigurationSettings } from './agVantageConfigurationSettings';
import { AgTechConfigurationSettings } from './agTechConfigurationSettings';
import { AgvanceConfigurationSettings } from './agvanceConfigurationSettings';
import { IntellegoConfigurationSettings } from './intellegoConfigurationSettings';

interface PartnerIntegrationProps {
    partner: Partner;
    integration: PartnerIntegration;
    onCreate(request: CreatePartnerIntegrationRequest): Promise<any>;
    onUpdate(integration: PartnerIntegration): void;
    onRemove(): void;
}

interface FormDetails {
    showExternalId: boolean;
    showAuthCredentials: boolean;
    externalIdLabel: string;
    showImport: boolean;
}


function getFormDetails(partner: Partner): FormDetails {
    const details: FormDetails = {
        showAuthCredentials: true,
        showExternalId: true,
        externalIdLabel: "External ID",
        showImport: true
    };

    switch (partner) {
        case Partner.DTN:
            details.showExternalId = false;
            break;
        case Partner.Oakland:
            details.showAuthCredentials = false;
            details.externalIdLabel = "Site ID";
            break;
        case Partner.Agris:
            details.showExternalId = false;
            break;
        case Partner.EverAg:
            details.showExternalId = false;
            details.showAuthCredentials = false;
            break;
        case Partner.AgVision:
            details.externalIdLabel = "CustomerDB";
            break;
        case Partner.AgVantage:
            details.showExternalId = false;
            details.showAuthCredentials = false;
            break;
        case Partner.AgTech:
            details.showExternalId = false;
            details.showImport = false;
            break
        case Partner.Intellego:
            details.showExternalId = false;
            details.showAuthCredentials = false;
            break;
    }

    return details;
}

const ManagePartnerIntegration: React.FC<PartnerIntegrationProps> = props => {

    const [showConfigureErpModal, setShowConfigureErpModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    const formDetails = getFormDetails(props.partner);

    if (formDetails == null) return null;

    return <>
        <Card className="mb-4">
            <Card.Body>
                <Card.Title>
                    {getPartnerName(props.partner)}
                    {props.integration == null && 
                        <button className="link-button" onClick={() => setShowConfigureErpModal(true)}>
                            <i className="material-icons">add</i>
                        </button>
                    }
                    {props.integration != null && 
                        <button className="link-button" onClick={() => props.onRemove()}>
                            <i className="material-icons">delete</i>
                        </button>
                    }
                </Card.Title>
                {props.integration && <div>
                    {formDetails.showExternalId && <div>
                        <strong className="mr-1">{formDetails.externalIdLabel}:</strong> 
                        <span>{props.integration.externalId}</span>
                    </div>}
                    {formDetails.showAuthCredentials && <div className="mt-1">
                        <strong>Auth Credentials:</strong> 
                        <AuthCredentials
                            partner={props.partner}
                            onChange={m => props.onUpdate({ ...props.integration, authenticationKey: m})} />
                    </div>}
                    <OtherConfiguration partner={props.partner} 
                    configuration={props.integration.configuration}
                    onChange={m => props.onUpdate({ ...props.integration, configuration: {... m}})} />
                    {formDetails.showImport && <div className="mt-1">
                        <Button onClick={() => setShowImportModal(true)}>Import mapping data...</Button>
                    </div>}
                    {showImportModal && <PartnerImportModal 
                        merchantId={props.integration.merchantId}
                        partner={props.partner}
                        onHide={() => setShowImportModal(false)}
                    />}
                </div>}
            </Card.Body>
        </Card>
        {showConfigureErpModal && <ConfigurePartnerIntegrationModal
            partner={props.partner}
            showAuthCredentials={formDetails.showAuthCredentials}
            showExternalId={formDetails.showExternalId}
            externalIdLabel={formDetails.externalIdLabel}
            onSave={props.onCreate}
            onHide={() => setShowConfigureErpModal(false)}
        />}
    </>
}

interface AuthCredentialsProps {
    partner: Partner;
    onChange(val: string): void;
}

const AuthCredentials: React.FC<AuthCredentialsProps> = props => {

    const [showEdit, setShowEdit] = useState(false);
    const [authenticationKey, setAuthenticationKey] = useState<string>('');

    if (!showEdit) {
        return <span> [hidden] <button className="link-button" onClick={() => setShowEdit(true)}>(change)</button></span>
    }

    function onSave() {
        props.onChange(authenticationKey);
        setShowEdit(false);
        setAuthenticationKey('');
    }

    function onCancel() {
        setShowEdit(false);
        setAuthenticationKey('');
    }

    return <span className="row">
        <div className="col-4">
            <EditAuthCredentials
                partner={props.partner}
                authenticationKey={authenticationKey}
                onChange={setAuthenticationKey} />
        </div>
        <div className="col-8">
            <button className="btn btn-primary"
                disabled={authenticationKey.length === 0}
                onClick={onSave}>
                    Save
            </button>
            <button className="btn btn-default ml-2"
                    onClick={onCancel}>Cancel</button>
        </div>
    </span>
}

interface OtherComponentProps<T = any> {
    partner: Partner;
    configuration: T;
    onChange(val: T): void;
}

const OtherConfiguration: React.FC<OtherComponentProps> = props => {
    
    switch(props.partner) {
        case Partner.AgTrax:
            return <AgTraxConfiguration {...props} />;
        case Partner.Agris:
            return <AgrisConfiguration {...props} />;
        case Partner.AgVantage:
            return <AgVantageConfiguration {...props} />;
        case Partner.AgTech:
            return <AgTechConfiguration {...props} />;
        case Partner.Agvance:
            return <AgvanceConfiguration {...props} />;
        case Partner.Intellego:
            return <IntellegoConfiguration {...props} />;
        default:
            return <></>;
    }
}

const AgTraxConfiguration: React.FC<OtherComponentProps<AgTraxConfigurationSettings>> = props => {
    return <div className="mt-1">
        <strong className="mr-1">URL:</strong> 
        <span>{props.configuration?.agTraxUrl}</span>
    </div>
}

const AgrisConfiguration: React.FC<OtherComponentProps<AgrisConfigurationSettings>> = props => {
    return <div className="mt-1">
        <div className="mr-1">
            <strong>Data Set:</strong> 
            <span>{props.configuration?.dataSet}</span>
        </div>
        {!props.configuration.useProxy && 
        <div className="mr-1">
            <strong>URL:</strong> 
            <span>{props.configuration?.url}</span>
        </div>}
        {props.configuration.useProxy && 
        <div className="mr-1">
            <strong>Proxy identifier:</strong> 
            <span>{props.configuration?.proxyRouteIdentifier}</span>
        </div>}
    </div>
}

const AgVantageConfiguration: React.FC<OtherComponentProps<AgVantageConfigurationSettings>> = props => {
    return <div className="mt-1">
        <div className="mr-1">
            <strong className="mr-1">Public Api Key:</strong> 
            <span>{props.configuration?.publicApiKey}</span>
        </div>
        <div className="mr-1">
            <strong className="mr-1">Private Api Key:</strong> 
            <span>********</span>
        </div>
        <div className="mr-1">
            <strong className="mr-1">Base Api URL:</strong> 
            <span>{props.configuration?.baseUrl}</span>
        </div>
    </div>
}

const AgTechConfiguration: React.FC<OtherComponentProps<AgTechConfigurationSettings>> = props => {
    return <div className="mt-1">
        <div className="mr-1">
            <strong className="mr-1">FTP URL:</strong> 
            <span>{props.configuration?.ftpAddress}:{props.configuration?.port}</span>
        </div>
        <div className="mr-1">
            <strong className="mr-1">SFTP:</strong> 
            <span>{props.configuration?.useSftp ? 'Yes' : 'No'}</span>
        </div>
        <div className="mr-1">
            <strong className="mr-1">Input folder:</strong> 
            <span>{props.configuration?.inputFolder}</span>
        </div>
        <div className="mr-1">
            <strong className="mr-1">Output folder:</strong> 
            <span>{props.configuration?.outputFolder}</span>
        </div>
    </div>
}

const AgvanceConfiguration: React.FC<OtherComponentProps<AgvanceConfigurationSettings>> = props => {
    return <><div className="mt-1 form-inline">
            <Form.Label className="pr-2">Use ERP Name to map Futures Month</Form.Label>
            <Form.Check
                type="switch"
                id="use-futures-month-erp-name-switch"
                checked={props.configuration?.useFuturesMonthErpName}
                onChange={ev => props.onChange({ ...props.configuration, useFuturesMonthErpName: ev.target.checked})}
            />
        </div>
        <div className="mt-1 form-inline">
            <Form.Label className="pr-2">Use Premium Discount Schedules</Form.Label>
            <Form.Check
                type="switch"
                id="use-premium-discount-schedules-switch"
                checked={props.configuration?.usePremiumDiscountSchedules}
                onChange={ev => props.onChange({ ...props.configuration, usePremiumDiscountSchedules: ev.target.checked})}
            />
        </div>
        </>
}

const IntellegoConfiguration: React.FC<OtherComponentProps<IntellegoConfigurationSettings>> = props => {
    return <div className="mt-1">
        <div className="mr-1">
            <strong className="mr-1">Database Connection String:</strong> 
            <span>********</span>
        </div>
    </div>
}

export default ManagePartnerIntegration